/**
 * After a deploy, an open tab still asks for the old hashed chunks, which are gone (server.mjs answers 404). Vite
 * raises `vite:preloadError`, or a dynamic import rejects. Either way the fix is one reload onto the new build.
 * A sessionStorage stamp makes it once per cooldown, so a chunk that is broken for real can't loop the tab.
 */

const KEY = 'cpg:stale-chunk-reload-at'
/** A second stale chunk inside this window shows the error screen instead of reloading again. */
export const STALE_CHUNK_COOLDOWN_MS = 60_000

const CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed/i

/** Whether an error is a chunk that failed to load, as opposed to a bug in the code. */
export function isChunkError(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const { name, message } = e as { name?: unknown; message?: unknown }
  return name === 'ChunkLoadError' || (typeof message === 'string' && CHUNK_ERROR.test(message))
}

export interface ReloadDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  now: () => number
  reload: () => void
}

const browserDeps = (): ReloadDeps => ({ storage: sessionStorage, now: Date.now, reload: () => location.reload() })

/** Reloads unless it already did within the cooldown. Returns whether it reloaded. */
export function reloadOnce(deps: ReloadDeps = browserDeps()): boolean {
  const now = deps.now()
  let last = Number.NaN
  try {
    last = Number(deps.storage.getItem(KEY))
  } catch {
    // storage blocked: fall through and allow one reload
  }
  if (Number.isFinite(last) && last > 0 && now - last < STALE_CHUNK_COOLDOWN_MS) return false
  try {
    deps.storage.setItem(KEY, String(now))
  } catch {
    // storage blocked or full: without a stamp we could loop, so don't reload
    return false
  }
  deps.reload()
  return true
}

/**
 * Listens for stale chunks on the window: Vite's preload error, and a dynamic import that rejects with nobody to
 * catch it (main.tsx's boot). Returns the unsubscribe. Call once, before the first render.
 */
export function installStaleChunkReload(target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window, deps?: ReloadDeps): () => void {
  const onPreload = (e: Event) => {
    // preventDefault stops Vite throwing; only when we reload, so a second failure still reaches the error screen.
    if (reloadOnce(deps)) e.preventDefault()
  }
  const onRejection = (e: Event) => {
    const reason = (e as PromiseRejectionEvent).reason
    if (isChunkError(reason) && reloadOnce(deps)) e.preventDefault()
  }
  target.addEventListener('vite:preloadError', onPreload)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('vite:preloadError', onPreload)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}
